import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, Smile, Meh, Frown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import PageHeader from '@/components/PageHeader';
import { createDailyReport, getDailyReports } from '@/utils/api';
import { toast } from 'sonner';

export default function ReportGiornaliero({ patient }) {
  const [todayReport, setTodayReport] = useState(null);
  const [formData, setFormData] = useState({
    stato: '',
    umore: '',
    sintomi: [],
    note: '',
  });
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const loadTodayReport = async () => {
      try {
        const response = await getDailyReports(patient.id);
        const todayData = response.data.find((r) => r.data === today);
        setTodayReport(todayData);

        if (todayData) {
          setFormData({
            stato: todayData.stato,
            umore: todayData.umore || '',
            sintomi: todayData.sintomi || [],
            note: todayData.note || '',
          });
        }
      } catch (error) {
        console.error('Errore nel caricamento report:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTodayReport();
  }, [patient.id, today]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.stato) {
      toast.error('Seleziona lo stato');
      return;
    }

    try {
      await createDailyReport({
        patient_id: patient.id,
        data: today,
        ...formData,
      });

      toast.success('Report salvato con successo!');
      const response = await getDailyReports(patient.id);
      const todayData = response.data.find((r) => r.data === today);
      setTodayReport(todayData);
    } catch (error) {
      console.error('Errore nel salvataggio report:', error);
      toast.error('Errore nel salvataggio');
    }
  };

  const statiOptions = [
    { value: 'ON', label: 'ON - Buona mobilità', color: 'green' },
    { value: 'OFF', label: 'OFF - Mobilità ridotta', color: 'orange' },
    { value: 'INTERMEDIO', label: 'Intermedio', color: 'blue' },
  ];

  const umoreOptions = [
    { value: 'ottimo', label: 'Ottimo', icon: Smile, color: 'green' },
    { value: 'buono', label: 'Buono', icon: Smile, color: 'blue' },
    { value: 'discreto', label: 'Discreto', icon: Meh, color: 'orange' },
    { value: 'difficile', label: 'Difficile', icon: Frown, color: 'red' },
  ];

  const sintomiOptions = [
    'Tremore',
    'Rigidità',
    'Bradicinesia',
    'Instabilità posturale',
    'Discinesia',
    'Congelamento',
    'Affaticamento',
  ];

  const toggleSintomo = (sintomo) => {
    setFormData((prev) => ({
      ...prev,
      sintomi: prev.sintomi.includes(sintomo)
        ? prev.sintomi.filter((s) => s !== sintomo)
        : [...prev.sintomi, sintomo],
    }));
  };

  if (loading) {
    return (
      <div className="page-container pb-24">
        <PageHeader title="Report Giornaliero" subtitle="Registra il tuo stato" icon={ClipboardList} />
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container pb-24">
      <PageHeader
        title="Report Giornaliero"
        subtitle={new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
        icon={ClipboardList}
      />

      {todayReport && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6"
        >
          <p className="text-green-800 font-medium text-center">Hai già compilato il report di oggi. Puoi aggiornarlo.</p>
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white rounded-3xl p-6 sm:p-8 soft-shadow border border-slate-200/60"
        >
          <Label className="text-lg font-bold text-slate-900 mb-4 block">Stato Motorio</Label>
          <div className="grid grid-cols-1 gap-3">
            {statiOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                data-testid={`btn-stato-${option.value.toLowerCase()}`}
                onClick={() => setFormData({ ...formData, stato: option.value })}
                className={`min-h-[64px] px-6 rounded-2xl font-semibold text-lg transition-all duration-300 ${
                  formData.stato === option.value
                    ? `bg-${option.color}-600 text-white shadow-lg scale-[0.98]`
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-white rounded-3xl p-6 sm:p-8 soft-shadow border border-slate-200/60"
        >
          <Label className="text-lg font-bold text-slate-900 mb-4 block">Umore</Label>
          <div className="grid grid-cols-2 gap-3">
            {umoreOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  data-testid={`btn-umore-${option.value}`}
                  onClick={() => setFormData({ ...formData, umore: option.value })}
                  className={`min-h-[72px] px-4 rounded-2xl font-semibold transition-all duration-300 flex flex-col items-center justify-center gap-2 ${
                    formData.umore === option.value
                      ? `bg-${option.color}-600 text-white shadow-lg scale-[0.98]`
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  <Icon className="w-6 h-6" />
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="bg-white rounded-3xl p-6 sm:p-8 soft-shadow border border-slate-200/60"
        >
          <Label className="text-lg font-bold text-slate-900 mb-4 block">Sintomi Presenti</Label>
          <div className="flex flex-wrap gap-2">
            {sintomiOptions.map((sintomo) => (
              <button
                key={sintomo}
                type="button"
                data-testid={`btn-sintomo-${sintomo.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => toggleSintomo(sintomo)}
                className={`px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                  formData.sintomi.includes(sintomo)
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {sintomo}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="bg-white rounded-3xl p-6 sm:p-8 soft-shadow border border-slate-200/60"
        >
          <Label htmlFor="note" className="text-lg font-bold text-slate-900 mb-4 block">
            Note Aggiuntive (Opzionale)
          </Label>
          <Textarea
            id="note"
            data-testid="textarea-note"
            value={formData.note}
            onChange={(e) => setFormData({ ...formData, note: e.target.value })}
            className="min-h-[120px] text-base rounded-2xl resize-none"
            placeholder="Aggiungi eventuali osservazioni..."
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
        >
          <Button
            type="submit"
            data-testid="btn-save-report"
            className="w-full min-h-[64px] text-lg font-semibold rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[0.98]"
          >
            Salva Report
          </Button>
        </motion.div>
      </form>
    </div>
  );
}
